import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider
} from "@mui/material";
import { Tabs, Tab } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Delete } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { useState, useEffect } from "react";

import {
  getPlansDetails,
  updateSubscriptionPlan
} from "../api/users"; // adjust path

export default function ManageSubscriptionPlans() {

  const [plansData, setPlansData] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [featuresList, setFeaturesList] = useState([]);
  const [chargesList, setChargesList] = useState([]);
  const [planPrice, setPlanPrice] = useState("");
  const [loading, setLoading] = useState(false);

  /* -------------------- FETCH PLANS -------------------- */

  const fetchPlans = async () => {
    try {

      setLoading(true);

      const res = await getPlansDetails();

      if (res?.success) {
        setPlansData(res.data || []);
      }
      if (res?.data?.length > 0) {
        setSelectedPlan(res.data[0].id);
      }

    } catch (error) {
      console.error("Error fetching plans", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  /* -------------------- SET PLAN DATA -------------------- */
  console.log("plansData=-=-=-=>", plansData)

  useEffect(() => {

    if (!selectedPlan || !plansData.length) return;

    const plan = plansData.find((p) => p.id === selectedPlan);

    if (!plan) return;

    setPlanPrice(plan.plan_charges || "");

    setFeaturesList(
      Array.isArray(plan.features) && plan.features.length
        ? plan.features
        : [""]
    );

    const reviewPricing =
      plan?.interpretation_info?.review_pricing || [];

    setChargesList(
      reviewPricing.length
        ? reviewPricing.map((item) => ({
          count: item.count || "",
          charges: item.price || ""
        }))
        : [{ count: "", charges: "" }]
    );

  }, [selectedPlan, plansData]);
  /* -------------------- SAVE PLAN -------------------- */

  const handleSave = async () => {

    try {

      const payload = {
        plan_id: selectedPlan,

        plan_charges: Number(planPrice),

        features: featuresList.filter(
          (f) => f.trim() !== ""
        ),

        review_pricing: chargesList
          .filter((c) => c.count && c.charges)
          .map((c) => ({
            count: Number(c.count),
            price: Number(c.charges)
          }))
      };

      const res = await updateSubscriptionPlan(payload);

      if (res?.success) {
        fetchPlans();
      }

    } catch (error) {
      console.error("Update failed", error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Manage Subscription Plans</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ mt: 4 }}>

        <Typography variant="h4" fontWeight={600} mb={4}>
          Manage Subscription Plans
        </Typography>

        {/* PLAN SELECTOR */}


        <Box sx={{ mb: 4 }}>
          <Tabs
            value={selectedPlan || false}
            onChange={(e, newValue) => setSelectedPlan(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            {plansData.map((plan) => (
              <Tab
                key={plan.id}
                value={plan.id}
                label={
                  <Box sx={{ textAlign: "left" }}>
                    <Typography fontWeight={600}>
                      {plan.plan_name}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      ₹{plan.plan_charges}/month
                    </Typography>
                  </Box>
                }
                sx={{
                  textTransform: "none",
                  alignItems: "flex-start",
                  px: 6
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* MAIN CARD */}

        <Grid container spacing={3}>

          {/* FEATURES */}

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, borderRadius: 3 }}>

              <Typography variant="h6" mb={2}>
                Features
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {featuresList.map((item, index) => (

                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 1
                  }}
                >

                  <TextField
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => {
                      const updated = [...featuresList];
                      updated[index] = e.target.value;
                      setFeaturesList(updated);
                    }}
                  />

                  {index === featuresList.length - 1 && (
                    <IconButton
                      color="primary"
                      onClick={() =>
                        setFeaturesList([
                          ...featuresList,
                          ""
                        ])
                      }
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  )}

                  <IconButton
                    color="error"
                    onClick={() => {
                      const updated = featuresList.filter(
                        (_, i) => i !== index
                      );
                      setFeaturesList(updated);
                    }}
                  >
                    <Delete />
                  </IconButton>

                </Box>

              ))}

            </Card>
          </Grid>

          {/* PLAN PRICE */}

          <Grid item xs={12} md={4}>

            <Card sx={{ p: 3, borderRadius: 3 }}>

              <Typography variant="h6" mb={2}>
                Plan Pricing
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2
                }}
              >

                <TextField
                  sx={{ width: 160 }}
                  value={planPrice}
                  disabled
                  
                />

                <Typography color="text.secondary">
                  / month
                </Typography>

              </Box>

            </Card>

          </Grid>

          {/* INTERPRETATION CHARGES */}

          <Grid item xs={12} md={4}>

            <Card sx={{ p: 3, borderRadius: 3 }}>

              <Typography variant="h6" mb={2}>
                Interpretation Charges
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {chargesList.map((item, index) => (

                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 1
                  }}
                >

                  <TextField
                    size="small"
                    placeholder="Count"
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    value={item.count}
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === 'e' || e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const updated = [...chargesList];
                      updated[index].count = e.target.value;
                      setChargesList(updated);
                    }}
                    sx={{ width: "40%" }}
                  />

                  <TextField
                    size="small"
                    placeholder="Price"
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    value={item.charges}
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === 'e' || e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const updated = [...chargesList];
                      updated[index].charges = e.target.value;
                      setChargesList(updated);
                    }}
                    sx={{ width: "60%" }}
                  />

                  {index === chargesList.length - 1 && (
                    <IconButton
                      color="primary"
                      onClick={() =>
                        setChargesList([
                          ...chargesList,
                          { count: "", charges: "" }
                        ])
                      }
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  )}

                  <IconButton
                    color="error"
                    onClick={() => {
                      const updated = chargesList.filter(
                        (_, i) => i !== index
                      );
                      setChargesList(updated);
                    }}
                  >
                    <Delete />
                  </IconButton>

                </Box>

              ))}

            </Card>

          </Grid>

        </Grid>

        {/* SAVE BUTTON */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 5
          }}
        >

          <Button
            variant="contained"
            size="large"
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 3
            }}
            disabled={!selectedPlan}
            onClick={handleSave}
          >
            Save Plan
          </Button>

        </Box>

      </Container>
    </>
  );
}